<script setup lang="ts">

import type { ColorMetaField } from '@/types/metas'
import type { AcContactData, AcPotential, AcMilestone, AcTrackedPotential } from '@/types/crm'
import type { AcReminder } from '@/types/application'
import type { fetchedUser } from '@/types/fetchedUser'

import {ref, computed, onBeforeMount, onBeforeUnmount} from 'vue'
import { useFetchRealTimeTrackedPotential, postCRMReminder, getStrategyResponse, getActionMapResponse } from '@/composables/crmBasedFunctions'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { useGetDocs } from '@/mixins/modularFirebaseOperations'
import { formatUser, modalClosed, formatDate } from '@/mixins/visualFormatters'
import { updateSingleField, addSingleArrayItem, removeSingleArrayItem } from '@/mixins/modularFirebaseOperations'
import { trackLastUpdate } from '@/composables/loggers'
import resetBackground from "@/mixins/resetBackground";
import removeBackground from "@/mixins/removeBackground";

// Import available reusable components
import ModernStatusChip from '@/components/reusable/modernStatusChip.vue'
import StarPicker from '@/components/reusable/starPicker.vue'
import CommentListDisplayer from '@/components/reusable/commentListDisplayer.vue'
import AcMediumModal from '@/components/reusable/teleports/acMediumModal.vue'
import StatusBar from '@/components/reusable/statusBar.vue'
import ContactDisplayer from '@/components/reusable/contactDisplayer.vue'
import AddReminderForm from '@/components/crm-components/addReminderForm.vue'
import CommentField from '@/components/reusable/commentField.vue'
import EditableTitle from '@/components/reusable/editableTitle.vue'
import EditableTextarea from '@/components/reusable/editableTextarea.vue'
import UserDisplay from '@/components/reusable/userDisplay.vue'
import StatusChanger from '@/components/reusable/statusChanger.vue'
import MilestoneDisplayer from '@/components/crm-components/milestoneDisplayer.vue'
import AddMilestoneForm from '@/components/crm-components/addMilestoneForm.vue'
import AccaVueNotification from '@/components/reusable/accaVueNotification.vue'

const potentialId = useRoute().params.id

const { trackedPotential, isLoadingTrackedPotential } = useFetchRealTimeTrackedPotential(potentialId as string)
//Eslint ignore no-unused-vars
//eslint-disable-next-line
const { docs, isLoadingDocs } = useGetDocs(`crm/jobCategories/list`)

const isPickingNewCategory = ref(false)
const isAddingMilestone = ref(false)
const isAddingReminder = ref(false)
const isDisplayingPriortiySwitcher = ref(false)
const store = useStore()
const userProfile = computed(() => store.getters['user/userProfile'])
const formattedUser = computed(() => formatUser(userProfile.value))
const aiActive = ref(false)

const determinePriorityDisplay = computed(() => {
    if(trackedPotential.value?.priority === "LOW"){
        return "Düşük"
    }
    if(trackedPotential.value?.priority === "STANDARD"){
        return "Standart"
    } else if(trackedPotential.value?.priority === "HIGH"){
        return "Yüksek"
    }
    return "Standart"
})



const updateSinglePotentialField = async (field: string, value: string) => {
    await updateSingleField(
        `crm/tracked/list/${potentialId}`,
        field,
        value,
    )
    await trackLastUpdate(
        `crm/tracked/list/${potentialId}`,
        {
            date: new Date(),
            user: formattedUser.value
        }
    )
}

const updatePotentialCategories = async (category: ColorMetaField) => {
    await addSingleArrayItem(
        `crm/tracked/list/${potentialId}`,
        "potentialCategories",
        category
    )
    await trackLastUpdate(
        `crm/tracked/list/${potentialId}`,
        {
            date: new Date(),
            user: formattedUser.value
        }
    )
}

const addUserToPotential = async (user: fetchedUser) => {
    await addSingleArrayItem(
        `crm/tracked/list/${potentialId}`,
        "assignedTo",
        user
    )
    await trackLastUpdate(
        `crm/tracked/list/${potentialId}`,
        {
            date: new Date(),
            user: formattedUser.value
        }
    )
}

const addCommentToPotential = async (comment: string) => {

    const commentObj = {
        comment: comment,
        date: new Date(),
        user: formattedUser.value
    }

    await addSingleArrayItem(
        `crm/tracked/list/${potentialId}`,
        "comments",
        commentObj
    )
    await trackLastUpdate(
        `crm/tracked/list/${potentialId}`,
        {
            date: new Date(),
            user: formattedUser.value
        }
    )
}

const removeUserFromPotential = async (user: fetchedUser) => {
    await removeSingleArrayItem(
        `crm/tracked/list/${potentialId}`,
        "assignedTo",
        user
    )
    await trackLastUpdate(
        `crm/tracked/list/${potentialId}`,
        {
            date: new Date(),
            user: formattedUser.value
        }
    )
}

const handleContactAdded = async (contact: AcContactData) => {
    const currentContacts = trackedPotential.value?.contact || []
    const updatedContacts = [...currentContacts, contact]
    
    await updateSingleField(
        `crm/tracked/list/${potentialId}`,
        "contact",
        updatedContacts,
    )
    
    await trackLastUpdate(
        `crm/tracked/list/${potentialId}`,
        {
            date: new Date(),
            user: formattedUser.value
        }
    )
}

const handleContactDeleted = async (contact: AcContactData) => {
    if (!trackedPotential.value?.contact) return
    
    // Filter out the deleted contact
    const updatedContacts = trackedPotential.value.contact.filter(existingContact => {
        return !(existingContact.title === contact.title && 
                existingContact.emailAddress === contact.emailAddress &&
                existingContact.phoneNumber === contact.phoneNumber)
    })
    
    await updateSingleField(
        `crm/tracked/list/${potentialId}`,
        "contact",
        updatedContacts,
    )
    
    await trackLastUpdate(
        `crm/tracked/list/${potentialId}`,
        {
            date: new Date(),
            user: formattedUser.value
        }
    )
}

const handleContactUpdated = async (updatedContact: AcContactData, originalContact: AcContactData) => {
    if (!trackedPotential.value?.contact) return
    
    // Find and replace the original contact with the updated one
    const updatedContacts = trackedPotential.value.contact.map(contact => {
        // Compare contacts by their properties to find the match
        if (contact.title === originalContact.title && 
            contact.emailAddress === originalContact.emailAddress &&
            contact.phoneNumber === originalContact.phoneNumber) {
            return updatedContact
        }
        return contact
    })
    
    // Update the entire contact array
    await updateSingleField(
        `crm/tracked/list/${potentialId}`,
        "contact",
        updatedContacts,
    )
    
    await trackLastUpdate(
        `crm/tracked/list/${potentialId}`,
        {
            date: new Date(),
            user: formattedUser.value
        }
    )
}

// Potential actions

const addReminder = () => {

    console.log(trackedPotential.value?.assignedTo)
    
    if(trackedPotential.value?.assignedTo.length === 0){
        alert("Bu potansiyel için hiçbir sorumlu eklenmediği için hatırlatma ekleyemezsiniz. Lütfen önce sorumlu ekleyiniz.")
        return
    } 

    isAddingReminder.value = true

}

const handleReminderAdded = async (reminder: Partial<AcReminder>) => {
    

    if(trackedPotential.value?.assignedTo.length === 0){
        alert("Bu potansiyel için hiçbir sorumlu eklenmediği için hatırlatma ekleyemezsiniz. Lütfen önce sorumlu ekleyiniz.")
        return
    }

    try{
        await postCRMReminder(
            reminder.additionalMessage,
            reminder.date,
            trackedPotential.value as AcPotential,
            "tracked-detail"
        )
    } catch (error) {
        alert(`Hatırlatma eklenirken bir hata oluştu: ${error}. Lütfen bu hatayı yazılım ekibiniz ile paylaşın.`)
    } finally {
        isAddingReminder.value = false
        modalClosed()
    }
}

const handleMilestoneAdded = async (milestone: AcMilestone) => {
    try {
        await addSingleArrayItem(
            `crm/tracked/list/${potentialId}`,
            "milestones",
            milestone
        )
        await trackLastUpdate(
            `crm/tracked/list/${potentialId}`,
            {
                date: new Date(),
                user: formattedUser.value
            }
        )

    } catch (error) {
        alert("Bir hata oluştu. Lütfen daha sonra tekrar deneyiniz.")
    } finally {
        isAddingMilestone.value = false
        modalClosed()
    }
}

const handleMilestoneDeleted = async (milestone: AcMilestone) => {
    const confirmation = confirm("Bu teması silmek istediğinize emin misiniz?")
    if(confirmation){
    try {
        await removeSingleArrayItem(
            `crm/tracked/list/${potentialId}`,
            "milestones",
            milestone
        )
        await trackLastUpdate(
            `crm/tracked/list/${potentialId}`,
            {
                date: new Date(),   
                user: formattedUser.value
            }
        )
    } catch (error) {
        console.error(error)
        }
    }
}

const handlePotentialCategoryDeleted = async (categoryId: string) => {
    const category = trackedPotential.value?.potentialCategories.find((category: ColorMetaField) => category.id === categoryId)

    if(category){
        try {
            await removeSingleArrayItem(
                `crm/tracked/list/${potentialId}`,
                "potentialCategories",
                category
            )
            await trackLastUpdate(
                `crm/tracked/list/${potentialId}`,
                {
                    date: new Date(),
                    user: formattedUser.value
                }
            )

            
        } catch (error) {
            console.error(error)
        }
    }
}

// AI Features

const isValidForAI = () => {

    let hasEvaluation: boolean;
    console.log("Evaluation",trackedPotential.value?.evaluation)
    if(trackedPotential.value?.evaluation.length > 100){
        console.log("Evaluation is long enough")
        if(trackedPotential.value?.evaluation === 'Satış süreçleri, teknik konulara ve gerçek ihtiyaçlara dayandığı kadar müşterilerin kişisel özelliklerine, onları ne kadar tanıdığınıza ve müşterilerin size karşı yaklaşımına dayanır. Bu bölümde, müşterinin kişisel özellikleri, beklentileri ve bugüne kadar yaşanan temaslardaki tavırları konusunda detaylı bilgiler girebilirsiniz.'){
            hasEvaluation = false
        } else {
            hasEvaluation = true
        }
    } else {
        hasEvaluation = false
    }

    const hasDescription = trackedPotential.value?.description.length > 100
    const hasMilestones = trackedPotential.value?.milestones.length > 0
    const hasComments = trackedPotential.value?.comments.length > 0

    if(hasEvaluation && hasDescription && hasMilestones && hasComments){
        return true
    } else if (trackedPotential.value?.description.length > 350 && trackedPotential.value?.evaluation.length > 350){
        return true
    } else {
        return false
    }
}

const sendStrategyRequest = () => {
    const valid = isValidForAI()
    if(!valid){
        alert("AI altyapımız, yalnızca üzerinde çalışılmış ve gerekli bilgileri doldurulmuş potansiyeller için devreye girebilmektedir. Lütfen açıklama ve değerlendirme kısımlarını detaylandırarak, daha fazla güncelleme ve temas girerek tekrar deneyiniz.")
        return
    }
    aiActive.value = true
    getStrategyResponse(trackedPotential.value as AcTrackedPotential)
    setTimeout(() => {
        aiActive.value = false
    }, 2000)

}

const sendActionMapRequest = () => {
    const valid = isValidForAI()
    if(!valid){
        alert("AI altyapımız, yalnızca üzerinde çalışılmış ve gerekli bilgileri doldurulmuş potansiyeller için devreye girebilmektedir. Lütfen açıklama ve değerlendirme kısımlarını detaylandırarak, daha fazla güncelleme ve temas girerek tekrar deneyiniz.")
        return
    }
    aiActive.value = true
    getActionMapResponse(trackedPotential.value as AcTrackedPotential)
    setTimeout(() => {
        aiActive.value = false
    }, 2000)
}

const sendIntroMail = () => {
    alert("Tanıtım belgesi gönderme özelliği şu anda yapım aşamasındadır. Lütfen daha sonra tekrar deneyiniz.")
}

onBeforeMount(() => {
    removeBackground.methods.removeBackground()
})

onBeforeUnmount(() => {
    resetBackground.methods.resetBackground()
})

</script>

<template>
    <div class="main-container content-start">
        <acca-vue-notification 
        v-if="aiActive"
        message="Dansuk AI sizin için bir strateji oluşturuyor. Hazır olduğunda otomatik olarak 'Güncellemeler' kısmında göreceksiniz."
        selectedType="success"
        />
        <status-bar :backgroundColor="'#021640'" class="col-start-1 col-end-13 !w-full !mb-0 !mt-8" v-if="trackedPotential?.status === 'INACTIVE'">
                <p >Bu potansiyel "İnaktif" olarak işaretlenmiş. Teknik olarak, bu iş üzerinde değişiklik ve güncellemeler yapabilirsiniz. Ancak uygulama çapında daha verimli kullanılabilmesi için, güncelleme durumunda potansiyeli önce tekrar aktif hale getirmenizi öneririz.</p>
            </status-bar>
        <section v-if="!isLoadingTrackedPotential" class="two-column-container sidebar-right col-start-1 col-end-13 mt-14">

            <main>
                <header class="flex items-center gap-4 relative">
                    <modern-status-chip 
                    @delete-clicked="handlePotentialCategoryDeleted($event)"
                    :is-deletable="true" v-for="category in trackedPotential?.potentialCategories" :key="category.id"
                        :bg-colors="{ [category.id]: category.color }" :value="category.id" :text="category.name" />
                    <button class="ac-square-button bg-black hover-bg-[#021640] text-white rounded-lg text-xl" @click="isPickingNewCategory = true">+</button>
                    <status-changer changer-title="Kategori Seç" v-if="isPickingNewCategory"
                        :status-list="docs" :bg-colors="docs.map((category: any) => category.color)"
                        :is-changeable="true" ac-position="[400, 40]" @cancelled="isPickingNewCategory = false"
                        @updated="updatePotentialCategories($event)" />
                </header>
                <editable-title class="mt-1 !mb-8" @title-edited="updateSinglePotentialField('title', $event)"
                    :text="trackedPotential?.title" />
                <div class="w-full py-4 bg-[var(--box-color)] rounded-xl">
                    <div v-if="trackedPotential?.assignedTo" class="assignee-container">
                        <user-display 
                            @user-added="addUserToPotential($event)"
                            @user-removed="removeUserFromPotential($event)" class="px-4" :show-add-button="true"
                            add-button-text="Sorumlu Ekle"
                            empty-state-text="Bu potansiyel için hiçbir sorumlu eklenmemiş. Sorumlular ekleyerek bu potansiyeli daha iyi takip edebiliriz."
                            empty-state-icon="~/assets/icons/no-user.png" :allow-remove="true" :allow-add="true"
                            :show-user-names="false" :image-size="'smaller'" :users="trackedPotential?.assignedTo" />
                    </div>
                </div>
                <h2 class="subtitle !mt-[4.8rem] mb-6">Potansiyel Açıklaması</h2>

                <editable-textarea class="!mt-[2.4rem]" @edit-cancelled="() => { }"
                    @edit-completed="updateSinglePotentialField('description', $event)"
                    :text="trackedPotential?.description" />

                <h2 class="subtitle !mt-[4.8rem] mb-6">Potansiyel Değerlendirmesi</h2>
                <editable-textarea class="!mt-[2.4rem]" @edit-cancelled="() => { }"
                    @edit-completed="updateSinglePotentialField('evaluation', $event)"
                    :text="trackedPotential?.evaluation" />
                
                <div class="milestones-header flex items-center justify-between">
                    <h2 class="subtitle !mt-[4.8rem] mb-6">Temaslar</h2>
                    <button class="ac-add-button text-2xl" @click="isAddingMilestone = true">Yeni Temas Ekle</button>
                </div>

                <milestone-displayer v-if="trackedPotential?.milestones && trackedPotential.milestones.length > 0" :milestones="trackedPotential.milestones" :icon-size="'3rem'" @delete-milestone="handleMilestoneDeleted($event)" />
                <p v-else class="text-2xl italic text-gray-400 mb-6">Bu potansiyel için hiçbir temas eklenmemiş. Önemli süreçler yaşandıkça yeni temaslar ekleyebilirsiniz.
                </p>

                <h2 class="subtitle !mt-[4.8rem] mb-6">İletişim Bilgileri</h2>

                <div class="contact-section">
                    <contact-displayer 
                        :cols="3"
                         :is-multiple="true" :is-editable="true" :is-deletable="true"
                        :show-icons="true" :show-qr-code="true" :contact-qr-title="trackedPotential?.title"
                        :show-add-button="true"
                        :contact-data="trackedPotential?.contact" @contact-updated="handleContactUpdated"
                        @contact-deleted="handleContactDeleted($event)" 
                        @contact-added="handleContactAdded($event)" />
                </div>


                <h2 class="subtitle !mt-[4.8rem] mb-6">Güncellemeler</h2>
                <div class="updates-container">
                    <div v-if="trackedPotential?.comments.length > 0" class="comments-container">
                        <comment-list-displayer :comments="trackedPotential?.comments" />
                    </div>
                    <div v-else>
                        <p class="text-2xl italic text-gray-400 mb-6">Bu potansiyel için herhangi bir güncelleme
                            eklenmemiş. Potansiyeli daha verimli takip etmek için belli aralıklarla güncellemeler
                            paylaşabilirsiniz.</p>
                    </div>
                </div>
                <comment-field class="!mt-[4.8rem] w-3/4 pb-24" @comment-submitted="addCommentToPotential($event)"
                    :text="trackedPotential?.comments"
                    placeholder="Yorumunuzu buraya yazın veya iptal etmek için 'esc' tuşuna basın." />

            </main>
            <aside v-if="!isLoadingTrackedPotential" class="">
                <h2 class="subtitle mb-6">İşlemler</h2>
                <div class="potential-buttons">
                    <button @click="sendStrategyRequest" class="ac-primary-button w-full text-2xl mb-4">
                        Strateji Oluştur
                    </button>
                    <button @click="sendActionMapRequest" class="ac-primary-button w-full text-2xl mb-4">
                        Aksiyon Haritası Çıkart
                    </button>
                    <button @click="addReminder()" class="ac-secondary-button w-full text-2xl mb-4">
                        Hatırlatma Ekle
                    </button>
                    <button class="ac-secondary-button w-full text-2xl mb-4" @click="sendIntroMail()">
                        Tanıtım Belgesi Gönder
                    </button>
                    <button v-if="trackedPotential?.status === 'ACTIVE'" @click="updateSinglePotentialField('status', 'INACTIVE')"
                        class="ac-secondary-button w-full text-2xl mb-4">
                        Deaktif Hale Getir
                    </button>
                    <button v-if="trackedPotential?.status === 'INACTIVE'" @click="updateSinglePotentialField('status', 'ACTIVE')"
                        class="ac-secondary-button w-full text-2xl mb-4">
                        Aktif Hale Getir
                    </button>
                </div>
                <div
                    class="potential-information mt-[3.6rem] border border-gray-300 rounded-xl p-4 flex flex-col gap-6">
                    <div class="flex items-center justify-between">
                        <h4 class="mb-0">Potansiyel Derecesi</h4>
                        <star-picker :picked-quantity="trackedPotential?.potentialRating!" :max-quantity="5"
                            @update:picked-quantity="updateSinglePotentialField('potentialRating', $event)" />
                    </div>
                    <hr />
                    <div class="flex items-center gap-2 text-2xl">
                        <p class="mb-0">Potansiyel Tipi:</p>
                        <p class="mb-0">{{ trackedPotential?.type === "company" ? "Şirket" : "Kişi" }}</p>
                    </div>
                    <div class="flex items-center gap-2 text-2xl relative">
                        <p class="mb-0">Öncelik:</p>
                        <select v-if="isDisplayingPriortiySwitcher" @change="updateSinglePotentialField('priority', $event.target.value)" class="absolute top-0 left-0 w-full h-full opacity-0">
                           <option value="LOW">Düşük</option>
                           <option value="STANDARD">Standart</option>
                           <option value="HIGH">Yüksek</option>
                        </select>
                        <p @click="isDisplayingPriortiySwitcher = !isDisplayingPriortiySwitcher" class="mb-0 cursor-pointer">{{ determinePriorityDisplay }}</p>
                    </div>
                    <div v-if="trackedPotential?.referredBy" class="flex items-center gap-2 text-2xl">
                        <p class="mb-0">Keşfeden:</p>
                        <p class="mb-0">{{ trackedPotential?.referredBy.userName }}</p>
                    </div>
                    <div v-if="trackedPotential?.thirdPartyRefferrer" class="flex items-center gap-2 text-2xl">
                        <p class="mb-0">Öneren: </p>
                        <p class="mb-0">{{ trackedPotential.thirdPartyRefferrer }}</p>
                    </div>
                </div>

                <div class="potential-meta mt-[3.6rem] border border-gray-300 rounded-xl p-4 flex flex-col gap-6">
                    <h3 class="mb-0">Ekleme Bilgileri</h3>
                    <div class="flex items-center gap-2 text-2xl">
                        <img :src="trackedPotential?.addedBy.profileImage" referrerpolicy="no-referrer" alt="Profile Image"
                            class="rounded-image-mini rounded-full" />
                        <p class="mb-0">{{ trackedPotential?.addedBy.userName }}</p>
                    </div>
                    <div class="flex items-center gap-3 text-2xl">
                        <img class="w-8 filter-main" src="../../assets/icons/calendar.png" alt="Calendar Icon">
                        <p class="mb-0">{{ formatDate(trackedPotential?.createdAt.toDate()) }}</p>
                    </div>
                    <h3 class="mb-0 mt-8">Takibe Alınma Bilgileri</h3>
                    <div class="flex items-center gap-2 text-2xl">
                        <img :src="trackedPotential?.trackedBy.profileImage" referrerpolicy="no-referrer" alt="Profile Image"
                            class="rounded-image-mini rounded-full" />
                        <p class="mb-0">{{ trackedPotential?.trackedBy.userName }}</p>
                    </div>
                    <div class="flex items-center gap-3 text-2xl">
                        <img class="w-8 filter-main" src="../../assets/icons/calendar.png" alt="Calendar Icon">
                        <p class="mb-0">{{ formatDate(trackedPotential?.trackedAt.toDate()) }}</p>
                    </div>
                    <hr class="my-8" />
                    <div class="latest-update-container">
                        <h3 class="mb-4">Son Güncelleme</h3>
                        <div class="flex items-center gap-2 text-2xl">
                            <img :src="trackedPotential?.updatedAt.user.profileImage" referrerpolicy="no-referrer" alt="Profile Image" class="rounded-image-mini rounded-full" />
                            <p class="mb-0">{{ trackedPotential?.updatedAt.user.userName }}</p>
                            <span> - </span>
                            <p class="mb-0">{{ formatDate(trackedPotential?.updatedAt.date.toDate()) }}</p>
                        </div>
                    </div>


                </div>



            </aside>
        </section>

        <teleport to="body"> 
            <transition
                appear
                type="animation"
                enter-active-class="animate__animated animate__fadeIn animate__fast"
                leave-active-class="animate__animated animate__fadeOut animate__faster"
            >
                <ac-medium-modal @modal-closed="isAddingReminder = false; modalClosed()" class="lg:!top-44 overflow-y-scroll" v-if="isAddingReminder" title="Hatırlatma Ekle" width="50vw" height="fit-content">
                    <add-reminder-form @reminder-added="handleReminderAdded($event)" @cancelled="isAddingReminder = false; modalClosed();"  />
                </ac-medium-modal>
            </transition>
        </teleport>

        <teleport to="body"> 
            <transition
                appear
                type="animation"
                enter-active-class="animate__animated animate__fadeIn animate__fast"
                leave-active-class="animate__animated animate__fadeOut animate__faster"
            >
                <ac-medium-modal @modal-closed="isAddingMilestone = false; modalClosed()" class="lg:!top-44 overflow-y-scroll" v-if="isAddingMilestone" title="Yeni Temas Ekle" width="50vw" height="fit-content">
                    <add-milestone-form @milestone-added="handleMilestoneAdded" @cancelled="isAddingMilestone = false; modalClosed();"  />
                </ac-medium-modal>
            </transition>
        </teleport>
    </div>
</template>

<style lang="scss" scoped>
@import "../../styles/global/helpers.scss";
@import "../../styles/core/variables.scss";
@import "../../styles/core/typography.scss";
@import "../../styles/global/background.scss";
@import "../../styles/global/buttons.scss";

.contact-section {
    width: 100%;
}
</style>